import { zodResolver } from '@hookform/resolvers/zod';
import { Layers } from 'lucide-react';
import { useState } from 'react';
import { useForm, type DefaultValues } from 'react-hook-form';
import { useCreatePlan } from '../hooks/usePlanMutations';
import {
  createPlanSchema,
  PLAN_VISIBILITIES,
  type CreatePlanFormValues,
} from '../schemas/plan-schema';
import { PlanVisibility, type CreatePlanRequest } from '../types/plans.types';
import { getVisibilityLabel } from '../utils/plan-sync-status';
import { PlanLimitsFields } from './PlanLimitsFields';
import { PlanPricePreviewDialog } from './PlanPricePreviewDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import { FormControl, FormField, FormItem } from '@/shared/components/ui/form';
import { Input, LabelVariants, MessageVariants } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { Textarea } from '@/shared/components/ui/textarea';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

interface CreatePlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Los campos numéricos arrancan vacíos, no en cero. Con un 0 de arranque había que borrarlo
 * antes de escribir —y quedaba pegado delante de la cifra si no— y, peor, un límite que nadie
 * tocó se enviaba como un cero deliberado.
 */
const DEFAULT_VALUES: DefaultValues<CreatePlanFormValues> = {
  name: '',
  description: '',
  priceMonthly: undefined,
  priceAnnual: undefined,
  visibility: PlanVisibility.Public,
  maxUsers: undefined,
  maxRegions: undefined,
  maxActiveTopics: undefined,
  maxActiveActors: undefined,
  maxAiAnalysisMonthly: undefined,
  maxAlerts: undefined,
  maxScheduledReports: undefined,
};

export function CreatePlanModal({ open, onOpenChange }: CreatePlanModalProps) {
  const { createPlan, isCreating } = useCreatePlan();

  const form = useForm<CreatePlanFormValues>({
    resolver: zodResolver(createPlanSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const selectedVisibility = form.watch('visibility');

  // El precio se captura sin IVA y el catálogo lo muestra con IVA, así que el administrador
  // publicaba un importe distinto al que había escrito y lo descubría después.
  const [pendingPlan, setPendingPlan] = useState<CreatePlanFormValues | null>(null);

  const handleClose = () => {
    if (!isCreating) {
      form.reset(DEFAULT_VALUES);
      onOpenChange(false);
    }
  };

  const onSubmit = (values: CreatePlanFormValues) => setPendingPlan(values);

  const handleConfirmCreate = () => {
    if (!pendingPlan) return;

    const payload: CreatePlanRequest = {
      ...pendingPlan,
      description: pendingPlan.description || undefined,
    };

    createPlan(payload, {
      onSuccess: () => {
        Notification.success({ title: 'Plan creado exitosamente' });
        setPendingPlan(null);
        form.reset(DEFAULT_VALUES);
        onOpenChange(false);
      },
    });
  };

  return (
    <ModalForm
      open={open}
      headerIcon={<Layers />}
      onOpenChange={handleClose}
      title="Crear plan"
      description="Define los precios y los límites del plan."
      className="sm:max-w-[560px]"
      data-testid="create-plan-modal"
      form={form}
      onSubmit={onSubmit}
      footer={
        <ModalFooter>
          <div className="flex flex-row justify-end w-full gap-4">
            <UnifiedButton
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isCreating}
              data-testid="cancel-create-plan-btn"
            >
              Cancelar
            </UnifiedButton>
            <UnifiedButton
              type="submit"
              variant="outline"
              actionType="submit"
              disabled={isCreating}
              data-testid="save-plan-btn"
            >
              Guardar
            </UnifiedButton>
          </div>
        </ModalFooter>
      }
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input
                label="Nombre"
                placeholder="Ej: Professional"
                disabled={isCreating}
                error={form.formState.errors.name?.message}
                data-testid="plan-name-input"
                {...field}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          // relative: LabelVariants es absolute, y sin ancestro posicionado la etiqueta se
          // anclaba al DialogContent y aterrizaba sobre el titulo del modal.
          <FormItem className="relative flex w-full flex-col gap-1">
            <label className={LabelVariants()}>Descripción</label>
            <FormControl>
              <Textarea
                placeholder="Opcional"
                rows={2}
                disabled={isCreating}
                data-testid="plan-description-input"
                {...field}
              />
            </FormControl>
            {form.formState.errors.description?.message && (
              <span className={MessageVariants({ variant: 'error' })}>
                {form.formState.errors.description.message}
              </span>
            )}
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="visibility"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <SelectInput
                label="Visibilidad"
                value={field.value}
                onValueChange={field.onChange}
                disabled={isCreating}
                placeholder="Seleccionar visibilidad"
                error={form.formState.errors.visibility?.message}
                data-testid="plan-visibility-select"
              >
                {PLAN_VISIBILITIES.map((visibility) => (
                  <SelectItem key={visibility} value={visibility}>
                    {getVisibilityLabel(visibility)}
                  </SelectItem>
                ))}
              </SelectInput>
            </FormControl>
          </FormItem>
        )}
      />

      {selectedVisibility === PlanVisibility.Private && (
        <p data-testid="plan-private-hint" className="text-primary text-xs">
          Un plan privado no aparece en el catálogo. Solo llega a una cuenta si un administrador se
          lo asigna.
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField
          control={form.control}
          name="priceMonthly"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  label="Precio mensual (MXN)"
                  placeholder="Ingresar precio"
                  type="number"
                  min={0}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)
                  }
                  disabled={isCreating}
                  error={form.formState.errors.priceMonthly?.message}
                  data-testid="plan-price-monthly-input"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="priceAnnual"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  label="Precio anual (MXN)"
                  placeholder="Ingresar precio"
                  type="number"
                  min={0}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)
                  }
                  disabled={isCreating}
                  error={form.formState.errors.priceAnnual?.message}
                  data-testid="plan-price-annual-input"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <p className="text-primary text-xs">Límites del plan</p>
      <PlanLimitsFields form={form} disabled={isCreating} testIdPrefix="plan" />
      <PlanPricePreviewDialog
        plan={pendingPlan}
        isPending={isCreating}
        onCancel={() => setPendingPlan(null)}
        onConfirm={handleConfirmCreate}
      />
    </ModalForm>
  );
}
